import React from 'react';
import { Box, Typography, Stack, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CustomModal from '../../CustomModal';
import Failed from '../../../assets/transaction/Failed.png';
import Success from '../../../assets/transaction/Success.png';
import Pending from '../../../assets/transaction/Pending.png';
import Copy from '../../../assets/transaction/coppy.png';
import { formatTransactionDate } from '../../../utils/formatTime';
import useLocales from '../../../hooks/useLocales';

const DetailRow = ({ label, value, showCopy }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center', // Ensures vertical centering
        py: 1.5,
        borderBottom: '1px solid #F4F6F8',
      }}
    >
      <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>{translate(label)}</Typography>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'right' }}>{translate(value)}</Typography>

        {showCopy && (
          <IconButton size="small" onClick={() => handleCopy(value)} sx={{ color: '#F59E0B', p: 0.5 }}>
            <img src={Copy} width="18" height="18" alt="copy icon" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
};

const TransactionDetails = ({ open, onClose, data, embedded = false }) => {
  const { translate } = useLocales();
  const { memberInfo } = useSelector((x) => ({
    ...x.providers,
    ...x.lookup,
    ...x.game,
  }));
  if (!data) return null;

  const isFailed = ['Failed', 'Timeout', 'Canceled', 'Rejected'].includes(data.status);

  const getStatusAssets = (status) => {
    if (status === 'Approve' || status === 'Completed' || status === 'Waiting_bank') {
      return { icon: Success, color: '#000000', label: 'Successful' };
    }
    if (status === 'Pending') {
      return { icon: Pending, color: '#000000', label: 'Pending' }; // Use an orange icon
    }
    // Default to Failed/Timeout/Canceled
    return { icon: Failed, color: '#000000', label: 'Failed' };
  };

  const statusAssets = getStatusAssets(data.status);

  const Content = (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
      {/* Centered Header Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // Centers children horizontally
          textAlign: 'center',
          mb: 3,
        }}
      >
        <Box component="img" src={statusAssets.icon} alt="status" sx={{ width: 80, height: 80, mb: 1 }} />
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: statusAssets.color }} // Dynamic status color
        >
          {translate(statusAssets.label)}
        </Typography>
      </Box>

      <Stack spacing={1}>
        <DetailRow label="transaction_id" value={data.transaction_id} showCopy />
        <DetailRow label="transaction_type" value={data.type} />
        <DetailRow label="Amount" value={`${memberInfo.currency} ${data.amount}`} />
        {/* DEPOSIT SPECIFIC FIELDS */}
        {/* {data.type === 'Deposit' && (
          <>
            <DetailRow label="Deposit Channel" value={data.depositChannel} />
            <DetailRow label="Bank" value={data.bank} />
            <DetailRow label="Promotion" value={data.promotion} />
            <DetailRow label="Bonus Amount" value={`MYR ${data.bonusAmount}`} />
          </>
        )} */}

        {/* WITHDRAWAL SPECIFIC FIELDS */}
        {/* {data.type === 'Withdrawal' && (
          <>
            <DetailRow label="Bank" value={data.bank} />
            <DetailRow label="Withdrawal Account" value={data.withdrawalAccount} />
          </>
        )} */}

        {/* TRANSFER SPECIFIC FIELDS */}
        {/* {data.type === 'Transfer' && (
          <>
            <DetailRow label="Transfer to" value={data.transferTo} />
            <DetailRow label="Received From" value={data.receivedFrom} />
          </>
        )} */}

        <DetailRow label="Date & Time" value={formatTransactionDate(data.created_at)} />

        {/* CONDITIONAL REASON FIELD */}
        {/* {isFailed && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>Reason</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#DC2626', textAlign: 'right' }}>
              {data.reason || 'Processing Error'}
            </Typography>
          </Box>
        )} */}
      </Stack>
    </Box>
  );

  if (embedded) return Content;

  return (
    <CustomModal open={open} onClose={onClose} maxWidth="xs" hasClose={true}>
      {Content}
    </CustomModal>
  );
};

export default TransactionDetails;
